import { useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Columns3,
  GripVertical,
  LoaderCircle,
  Pause,
  Play,
  Rows3,
  Search,
} from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Dialog, EmptyState, ErrorState, Field, LoadingState, PageHeader, STATUS_LABELS, StatusBadge } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import { useRouter } from "../routing/Router";
import type { BoardItem, WorkflowStatus } from "../types";

const BOARD_COLUMNS: WorkflowStatus[] = [
  "ideas",
  "ready",
  "queued",
  "producing",
  "ready_to_upload",
  "published",
  "needs_attention",
];

export function WorkPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { session } = useAuth();
  const { navigate } = useRouter();
  const work = useAsyncData(() => client.board.get(), [client]);
  const [view, setView] = useState<"board" | "table">("table");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | "all">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");

  const canManage = session?.user.role === "owner" || session?.user.role === "manager";
  const visibleItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (work.data?.items ?? []).filter(
      (item) =>
        (statusFilter === "all" || item.status === statusFilter) &&
        (!normalized ||
          item.title.toLowerCase().includes(normalized) ||
          item.channelName.toLowerCase().includes(normalized)),
    );
  }, [query, statusFilter, work.data]);

  async function reorder(itemId: string, direction: -1 | 1) {
    if (!work.data) return;
    const queued = work.data.items
      .filter((item) => item.type === "job" && item.status === "queued")
      .sort((a, b) => (a.queuePosition ?? 0) - (b.queuePosition ?? 0));
    const index = queued.findIndex((item) => item.id === itemId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= queued.length) return;
    [queued[index], queued[target]] = [queued[target], queued[index]];
    setSaving(itemId);
    try {
      await client.jobs.reorder(queued.map((item) => item.id));
      await work.reload();
    } finally {
      setSaving(null);
    }
  }

  async function startBatch() {
    const ids = [...selected].filter((id) => {
      const item = work.data?.items.find((candidate) => candidate.id === id);
      return item?.type === "job" && ["ready", "needs_attention"].includes(item.status);
    });
    if (!ids.length) return;
    setSaving("batch");
    setActionError(null);
    try {
      await client.jobs.start(ids);
      setSelected(new Set());
      await work.reload();
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : "The batch could not start.");
    } finally {
      setSaving(null);
    }
  }

  async function pauseGracefully() {
    setSaving("pause");
    setActionError(null);
    try {
      await client.jobs.pause(pauseReason.trim());
      setPauseOpen(false);
      setPauseReason("");
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : "The pause request failed.");
    } finally {
      setSaving(null);
    }
  }

  if (work.loading) return <LoadingState label="Loading work" />;
  if (work.error || !work.data) {
    return <ErrorState message={work.error ?? "Work is unavailable."} onRetry={work.reload} />;
  }
  const boardData = work.data;

  return (
    <>
      <PageHeader
        title="Work"
        description={canManage ? "Prioritize jobs, start selected work, and request a graceful pause." : "Track each video from idea to publication."}
        actions={
          canManage ? (
            <>
              <button className="button secondary" type="button" onClick={() => setPauseOpen(true)}>
                <Pause size={16} />
                Pause gracefully
              </button>
              <button className="button primary" type="button" disabled={!selected.size || saving === "batch"} onClick={() => void startBatch()}>
                {saving === "batch" ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}
                Start selected {selected.size ? `(${selected.size})` : ""}
              </button>
            </>
          ) : undefined
        }
      />
      <div className="toolbar">
        <label className="search-control">
          <Search size={16} aria-hidden="true" />
          <input aria-label="Search work" placeholder="Search title or channel" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <select aria-label="Filter by status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as WorkflowStatus | "all")}>
          <option value="all">All statuses</option>
          {BOARD_COLUMNS.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
        </select>
        <div className="segmented-control compact" aria-label="Work view">
          <button className={view === "table" ? "active" : ""} type="button" onClick={() => setView("table")} aria-label="Table view" title="Table view"><Rows3 size={16} /></button>
          <button className={view === "board" ? "active" : ""} type="button" onClick={() => setView("board")} aria-label="Board view" title="Board view"><Columns3 size={16} /></button>
        </div>
      </div>
      {actionError ? <div className="inline-alert" role="alert">{actionError}</div> : null}
      {!visibleItems.length ? (
        <EmptyState title="No matching work" message="Change the filter or add a new idea." />
      ) : view === "table" ? (
        <div className="table-wrap work-table-wrap">
          <table className="work-table">
            <thead>
              <tr>{canManage ? <th className="selection-cell">Select</th> : null}<th>Video</th><th>Status</th><th>Created</th>{canManage ? <th className="actions-cell">Order</th> : null}</tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={`${item.type}-${item.id}`}>
                  {canManage ? (
                    <td className="selection-cell">
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.title}`}
                        checked={selected.has(item.id)}
                        disabled={item.type !== "job" || !["ready", "needs_attention"].includes(item.status)}
                        onChange={(event) => setSelected((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        })}
                      />
                    </td>
                  ) : null}
                  <td>
                    {item.type === "job" ? (
                      <button className="title-link" type="button" onClick={() => navigate(`/jobs/${item.id}`)}>{item.title}</button>
                    ) : (
                      <strong>{item.title}</strong>
                    )}
                    <span className="table-subtext">{item.channelName}</span>
                  </td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  {canManage ? (
                    <td className="actions-cell">
                      {item.type === "job" && item.status === "queued" ? (
                        <div className="row-actions">
                          <button className="icon-button" type="button" title="Move up" aria-label={`Move ${item.title} up`} disabled={saving === item.id} onClick={() => void reorder(item.id, -1)}><ArrowUp size={16} /></button>
                          <button className="icon-button" type="button" title="Move down" aria-label={`Move ${item.title} down`} disabled={saving === item.id} onClick={() => void reorder(item.id, 1)}><ArrowDown size={16} /></button>
                        </div>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="board" aria-label="Workflow board">
          {BOARD_COLUMNS.map((status) => {
            const columnItems = visibleItems.filter((item) => item.status === status).sort((a, b) => (a.queuePosition ?? 0) - (b.queuePosition ?? 0));
            return (
              <section
                className="board-column"
                key={status}
                onDragOver={(event) => { if (status === "queued") event.preventDefault(); }}
                onDrop={() => {
                  if (!draggedId || status !== "queued") return;
                  const ids = [
                    ...boardData.items.filter((item) => item.type === "job" && item.status === "queued" && item.id !== draggedId).sort((a, b) => (a.queuePosition ?? 0) - (b.queuePosition ?? 0)).map((item) => item.id),
                    draggedId,
                  ];
                  void client.jobs.reorder(ids).then(work.reload);
                  setDraggedId(null);
                }}
              >
                <header><h2>{STATUS_LABELS[status]}</h2><span>{columnItems.length}</span></header>
                <div className="board-items">
                  {columnItems.map((item) => (
                    <article className="job-card" key={`${item.type}-${item.id}`} draggable={canManage && item.type === "job" && item.status === "queued"} onDragStart={() => setDraggedId(item.id)} onDragEnd={() => setDraggedId(null)}>
                      <div className="job-card-top">
                        {canManage && item.type === "job" && item.status === "queued" ? <GripVertical size={16} className="drag-handle" aria-hidden="true" /> : null}
                        {item.type === "job" ? <button className="title-link" type="button" onClick={() => navigate(`/jobs/${item.id}`)}>{item.title}</button> : <strong>{item.title}</strong>}
                      </div>
                      <span>{item.channelName}</span>
                      <div className="job-card-foot">
                        <small>{item.type === "idea" ? "Idea" : item.type === "history" ? "Imported history" : "Job"}</small>
                        {canManage && item.type === "job" && ["ready", "needs_attention"].includes(item.status) ? (
                          <input type="checkbox" aria-label={`Select ${item.title}`} checked={selected.has(item.id)} onChange={(event) => setSelected((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(item.id);
                            else next.delete(item.id);
                            return next;
                          })} />
                        ) : null}
                      </div>
                    </article>
                  ))}
                  {!columnItems.length ? <p className="column-empty">Nothing here</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {pauseOpen ? (
        <Dialog
          title="Pause gracefully"
          onClose={() => setPauseOpen(false)}
          actions={
            <>
              <button className="button secondary" type="button" onClick={() => setPauseOpen(false)}>Cancel</button>
              <button className="button primary" type="button" disabled={pauseReason.trim().length < 3 || saving === "pause"} onClick={() => void pauseGracefully()}>Request pause</button>
            </>
          }
        >
          <p>Active work is allowed to reach its next safe checkpoint.</p>
          <Field label="Reason" hint="At least 3 characters. This is recorded in the audit history.">
            <textarea rows={4} value={pauseReason} onChange={(event) => setPauseReason(event.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </>
  );
}

export function isStartable(item: BoardItem): boolean {
  return item.type === "job" && ["ready", "needs_attention"].includes(item.status);
}
